import './in_page_search_display.less';

import React, {
  Component,
  useEffect,
  useRef
} from 'react';

import PropTypes from 'prop-types';

import Mark from 'mark.js';

import find from 'lodash/find';
import includes from 'lodash/includes';
import map from 'lodash/map';
import noop from 'lodash/noop';

import FocusTrap from 'focus-trap-react';

import Select, { components }  from 'react-select';
import ComboBox from 'zoomforth_component_lib/combobox.jsx';

import { 
  getContentItemTypeIconName, 
  getContentItemType 
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  FileIcon,
  FileImageIcon,
  FilePdfIcon,
  FilePowerPointIcon,
  FileVideoishIcon,
  FormIcon,
  ListIcon,
  MenuIcon,
  SearchIcon,
  TextIcon,
  WebPreviewIcon
} from 'zoomforth_component_lib/external_modern_icons.jsx';

import withClickOutside from 'react-onclickoutside';

import SiteViewerVersionComponentSelector
  from 'zoomforth_codesbane_viewer/component_lib/site_viewer_version_component_selector_container.jsx';


const getDisplayableIconComponent = (iconName)=> {
  switch (iconName) {

  case 'image':
    return FileImageIcon;

  case 'menu':
    return MenuIcon;

  case 'pdf':
    return FilePdfIcon;

  case 'ppt':
    return FilePowerPointIcon;

  case 'form':
    return FormIcon;

  case 'accordion_item':
    return ListIcon;

  case 'file':
    return FileIcon;

  case 'text':
    return TextIcon;

  case 'videoish':
    return FileVideoishIcon;

  case 'web_preview':
    return WebPreviewIcon;

  default:
    throw new Error(`Unexpected icon name ${iconName}`);
  }
};


const getHighlighterInstance = (context) => {
  // https://markjs.io/
  const highlighterContext = document.querySelector(
    context
  );
  const highlighterInstance = new Mark(highlighterContext);
  return highlighterInstance;
};

const SearchResultItem = ({ matches, identifyingField, meta })=> {
  const { content_type, asset_type } = meta;
  const iconName = meta.type === 'pageContentItem' ?
      getContentItemTypeIconName(getContentItemType({ content_type, asset_type }))
    :
      'menu';
  const DisplayableIcon = getDisplayableIconComponent(iconName);

  return (
    <div className="search-result-item">
      <div className="result-item-icon-box">
        <DisplayableIcon />
      </div>
      <div className="result-item-content-box">
        <div>
          {
            (identifyingField && !find(matches, ['fieldName', identifyingField.fieldName])) &&
            <div className="search-result-match-item">
              <span className="field-name">{ identifyingField.displayableFieldName }:</span> 
              <span className="field-value" dangerouslySetInnerHTML={{__html: identifyingField.fieldValue }} />
            </div>
          }
          {
            map(matches, ({ matchedText, fieldName, displayableFieldName, score }, index)=>
              <div key={index} className="search-result-match-item-result">
                <span className="field-name">{ displayableFieldName }:</span>
                <span className='field-value'> { matchedText } </span>
              </div>
            )
          }
        </div>
        <div className="item-type-description">
          {
            meta.type === 'pageContentItem' && meta.description
          }
        </div>
      </div>
    </div>
  );
};


const CustomOption = ({ label, value, ...props })=> {
  return (
    <components.Option key={value} value={value} {...props}>
      <SearchResultItem
        matches={label.matches}
        score={label.score}
        identifyingField={label.identifyingField}
        meta={label.meta}
      />
    </components.Option>
  );
};

const CustomControl = ({ children, ...props })=>
  <components.Control {...props}>
    <SearchIcon />
    { children }
  </components.Control>;

const NoOptionsMessage = (props)=>
  <components.NoOptionsMessage {...props}>
    No results
  </components.NoOptionsMessage>;
  

const InPageSearchDisplay_ViewerVersion1 = ({ results, search, onSearchChange, onResultChoose })=> (
  <div className="full-text-search-display">
    <div className="full-text-search-display-results">

      <Select
        autoFocus={true}
        className="full-text-search-select"
        classNamePrefix="full-text-search-select" // creates all the 'full-text-seach-select__foobar' classNames
        components={{
          Control: CustomControl,
          Option: CustomOption,
          NoOptionsMessage: NoOptionsMessage
        }}
        filterOption={() => true} // https://github.com/JedWatson/react-select/issues/2865#issuecomment-508407878
        inputValue={search}
        menuIsOpen={true}
        options={map(results, ({ ident, identifyingField, matches, meta, score })=>
          ({
            value: ident,
            label: { matches, score, identifyingField, meta }
          })
        )}
        placeholder="Search in site..."
        value={null}
        onChange={({ value })=> {
          onResultChoose(value); // Value is `ident`
        }}
        onInputChange={(value, { action })=> {
          if (
            // Sometimes react-select wants to reset the value of the search when we dont
            // want to, i.e., when closing menu or selecting search items
            action === 'input-change'
          ) {
            onSearchChange(value);
          }
        }}
      />

    </div>
  </div>
);

const InPageSearchDisplay_ViewerVersion2 = ({ results, search, onSearchChange, onResultChoose })=> (
  <div className="full-text-search-display">
    <div className="full-text-search-display-results">

      <ComboBox
        label="Search in site"
        className="full-text-search-select"
        comboBoxIcon={<SearchIcon />}
        openOnFocus={true}
        openDisplayInPortal={false}
        placeholder="Search in site..."
        inputProps={{ value: search }}
        options={map(results, ({ ident, identifyingField, matches, meta, score })=>
          ({
            value: ident, 
            content: (
              <SearchResultItem
                meta={meta}
                score={score} 
                matches={matches} 
                identifyingField={identifyingField}
              />
            )
          })
        )}
        onSelect={(ident)=> {
          onResultChoose(ident);
        }}
        onInputChange={(e)=> {
          onSearchChange(e.target.value);
        }}
      />

    </div>
  </div>
);

const InPageSearchDisplay = (props) => (
  <SiteViewerVersionComponentSelector>
    <InPageSearchDisplay_ViewerVersion1 forSiteViewerVersion={1} { ...props } />
    <InPageSearchDisplay_ViewerVersion2 forSiteViewerVersion={2} { ...props } />
  </SiteViewerVersionComponentSelector>
);


const InPageSearchDisplayWithClose = withClickOutside(
  class InPageSearchDisplayWithClose extends Component {
    handleClickOutside() {
      this.props.onClose();
    }
    render() {
      return <InPageSearchDisplay {...this.props} />;
    }
  }
);


const InPageSearchDisplayWithBackgroundAndClose = ({
  results,
  search,
  ...props
}) => {

  // These persist across re-renders
  const didRenderOnce = useRef(false);
  const highlighterInstanceRef = useRef(null);

  useEffect(() => {
    /**
     * Mark will not update regardless of React prop updates so
     * we perform this manually.
     * 
     * We use a `done` callback here to ensure all els are
     * unmarked before we update the DOM again.
     */
    const unmarkResults = (done) => {
      highlighterInstanceRef.current.unmark({ done });
    };

    const markResults = () => {
      if (results) {
        highlighterInstanceRef.current.mark(search,
          { //options
            /**
             * markjs automagically
             * wraps our results in
             * a <mark> element.
             */
            className: 'highlight',
            ignoreJoiners: true,
            // We want to match the .search-result-match-item-result class
            exclude: [
              '.search-result-match-item',
              '.field-name',
              '.item-type-description'
            ]
          }
        );
      }
    };

    // We don't need to search and
    // unmark on initial render
    if (didRenderOnce.current) {
      unmarkResults(markResults);

    } else {
      highlighterInstanceRef.current =
        // Search the entire tree at once from here down
        getHighlighterInstance('.full-text-search-display-results');
      didRenderOnce.current = true;

      markResults();

      return ;
    }

    // We run this function whenever `results` changes
  }, [results]);

  return (
    <FocusTrap>
      <div className="full-text-search-modal-background">
        <InPageSearchDisplayWithClose
          results={results}
          search={search}
          {...props}
        />
      </div>
    </FocusTrap>
  );
};


InPageSearchDisplay.propTypes = {
  results: PropTypes.arrayOf(PropTypes.shape({
    ident: PropTypes.string.isRequired,
    identifyingField: PropTypes.shape({
      displayableFieldName: PropTypes.string.isRequired,
      fieldName: PropTypes.string.isRequired,
      fieldValue: PropTypes.string.isRequired
    }),
    matches: PropTypes.arrayOf(PropTypes.shape({
      displayableFieldName: PropTypes.string.isRequired,
      fieldName: PropTypes.string.isRequired,
      matchedText: PropTypes.string.isRequired,
      score: PropTypes.number.isRequired
    })),
    score: PropTypes.number.isRequired
  })).isRequired,
  search: PropTypes.string.isRequired,
  onResultChoose: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

InPageSearchDisplay.defaultProps = {
  search: ''
};


export default InPageSearchDisplayWithBackgroundAndClose;
